import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, tap, throwError } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import {
	NodeChangeType,
	ApolloQueryErrorMapper,
	CombiningSubscriptionManager,
	SubscriptionManagerService,
	ApolloQueryError
} from '@shure/shared/angular/data-access/system-api/core';
import {
	ChannelNoiseReductionLevel,
	Response,
	ResponseState
} from '@shure/shared/angular/data-access/system-api/models';
import { ILogger } from '@shure/shared/angular/utils/logging';
import { noMoreSubscribers } from '@shure/shared/typescript/rxjs';

import { NoiseReductionApiService, NoiseReductionChannel } from '../../api';
import {
	NoiseReductionChannelMutationGQL,
	NoiseReductionChannelQueryGQL,
	NoiseReductionChannelSubscriptionGQL
} from '../graphql/generated/noise-reduction.generated';
import { mapNoiseReductionChannelFromSysApi } from '../mappers';

@Injectable()
export class DaiDeviceUiDspNoiseReductionService implements NoiseReductionApiService {
	private readonly logger: ILogger;

	private readonly noiseReductionChannelSubscriptionManager: CombiningSubscriptionManager;

	constructor(
		logger: ILogger,
		private readonly noiseReductionChannelQueryGQL: NoiseReductionChannelQueryGQL,
		private readonly noiseReductionChannelMutationGQL: NoiseReductionChannelMutationGQL,
		noiseReductionChannelSubscriptionGQL: NoiseReductionChannelSubscriptionGQL,
		subscriptionManagerService: SubscriptionManagerService
	) {
		this.logger = logger.createScopedLogger('DaiDeviceUiDspNoiseReductionService');

		this.noiseReductionChannelSubscriptionManager = new CombiningSubscriptionManager(
			{
				name: 'DaiDeviceUiDspNoiseReductionService',
				changeTypes: [
					NodeChangeType.AudioChannelNoiseReduction,
					NodeChangeType.AudioChannelChannelConfiguration
				],
				subscriptionGql: noiseReductionChannelSubscriptionGQL
			},
			logger,
			subscriptionManagerService
		);
	}

	public getChannel$(channelId: string): Observable<NoiseReductionChannel> {
		const queryId = uuidv4();
		this.logger.trace('getChannel$()', 'Querying noise reduction channel', { channelId, queryId });

		return this.noiseReductionChannelQueryGQL
			.watch({ channelId }, { fetchPolicy: 'network-only' })
			.valueChanges.pipe(
				map((query) => {
					if (query.data?.node && 'features' in query.data.node) {
						this.logger.trace('getChannel$()', 'Query updated', { channelId });
						return mapNoiseReductionChannelFromSysApi(query.data.node);
					}

					this.logger.error('getChannel$()', 'Failed to query channel', { channelId });
					throw ApolloQueryErrorMapper.getError(query);
				}),
				tap((channel) => this.noiseReductionChannelSubscriptionManager.subscribe(queryId, channel.id)),
				noMoreSubscribers(() => {
					this.logger.trace('getChannel$', 'noMoreSubscribers', { channelId, queryId });
					this.noiseReductionChannelSubscriptionManager.unsubscribeQuery(queryId);
				}),
				catchError((error) => {
					return throwError(() => new ApolloQueryError(error));
				})
			);
	}

	public toggleNoiseReduction(channelId: string, enabled: boolean): Observable<Response<void, string>> {
		this.logger.trace('toggleNoiseReduction()', 'Toggling noise reduction', { channelId, enabled });
		return this.updateChannel(channelId, { enabled });
	}

	public setNoiseReductionLevel(
		channelId: string,
		level: ChannelNoiseReductionLevel
	): Observable<Response<void, string>> {
		this.logger.trace('setNoiseReductionLevel()', 'Setting noise reduction level', { channelId, level });
		return this.updateChannel(channelId, { level });
	}

	public setNoiseReductionIntensity(channelId: string, percentage: number): Observable<Response<void, string>> {
		this.logger.trace('setNoiseReductionIntensity()', 'Setting noise reduction intensity', {
			channelId,
			percentage
		});
		return this.updateChannel(channelId, { percentage });
	}

	private updateChannel(
		channelId: string,
		update: Partial<NoiseReductionChannel['features']['noiseReduction']>
	): Observable<Response<void, string>> {
		return this.noiseReductionChannelMutationGQL
			.mutate(
				{
					updates: {
						audioChannel: {
							id: channelId,
							features: {
								noiseReduction: {
									...update
								}
							}
						}
					},
					injectKeyring: '//supplied-by-keyring-injector'
				},
				{ errorPolicy: 'all' }
			)
			.pipe(
				map((mutation) => {
					if (
						mutation.data?.updateNodes &&
						'error' in mutation.data.updateNodes[0] &&
						mutation.data.updateNodes[0].error !== null
					) {
						this.logger.error('updateChannel()', 'Failed to update channel', {
							channelId,
							mutation
						});
						return { state: ResponseState.Error, error: mutation.data.updateNodes[0].error?.message };
					}

					this.logger.trace('updateChannel()', 'Done', { channelId });
					return { state: ResponseState.Done };
				}),
				catchError((error) => {
					this.logger.error('updateChannel()', 'Failed to update channel', { error });
					return of({ state: ResponseState.Error, error });
				})
			);
	}
}
